/**
 * FR-018.7 — typed client over the farmer "Sources" routes (`/v1/twin/sources/*`).
 *
 * Wire shapes: docs/architecture/TENANT_CONFIGURATION_UI.md §6 and the backend route
 * `backend/src/api/v1/endpoints/twin/sources.py`. Farmer actions are only
 * confirm | propose | reject | retire; `supersedes` is computed by the server.
 */
import api from './api';

export type SourceRowKind = 'confirmed' | 'shared' | 'suggested' | 'unassigned' | 'alias' | 'merge_suggested';

export interface SourceRowMember {
  entity_id: string;
  name: string;
}

/** One live confirmed span row of a shared label (`shared` rows' `span_rows`). */
export interface SourceSpanRow {
  mapping_id: string;
  target_ref: string;
  /** `null` when the target is tombstoned / missing (its row still exists). */
  name: string | null;
  valid_from: number | null;
  valid_to: number | null;
}

export interface SourceRowDto {
  kind: SourceRowKind;
  mapping_id: string | null;
  alias_id: string | null;
  source_system: string | null;
  source_label: string;
  /** `is` | `spans` for mapping rows; `null` for alias / unassigned rows. */
  relation: string | null;
  registry_kind: string | null;
  valid_from: number | null;
  valid_to: number | null;
  validity_kind: 'season' | 'global';
  status: string | null;
  origin: string | null;
  note: string | null;
  /** `shared` rows only: the OTHER live fields over overlapping seasons (display list). */
  members?: SourceRowMember[];
  /**
   * `shared` rows only: EVERY live confirmed span row of the label (this one included, disjoint
   * seasons included). Absent from an older backend.
   */
  span_rows?: SourceSpanRow[];
  evidence: string | null;
  counts: { documents?: number; picks?: number; kg?: number };
}

export interface SourcesObject {
  entity_type: string;
  entity_id: string;
  name: string;
  is_stub: boolean;
  stub: { source_system: string | null; source_label: string; first_seen: string | null } | null;
}

export type SourcesMaster =
  | {
      source_system: string | null;
      role_label: string | null;
      name: string | null;
      open_url: string | null;
      /** The master system's own older names of this object, in source order („früher: …“). */
      earlier_names?: string[];
    }
  | { origin: { document_id: string | null; created_at: string | null } };

export interface SourcesConfig {
  noun: string;
  master_role: string | null;
  unassigned_verb: 'assign' | 'merge' | string;
  suggestions: boolean;
  /** False when no mapping kind targets the type: the panel offers no „Referenz hinzufügen“. */
  addable?: boolean;
}

export interface SourcesResponse {
  object: SourcesObject;
  master: SourcesMaster;
  rows: SourceRowDto[];
  config: SourcesConfig;
}

export interface SourceMappingItem {
  action: 'confirm' | 'propose' | 'reject' | 'retire';
  kind: string;
  source_system: string;
  source_label: string;
  target_ref: string;
  target_kind?: 'entity' | 'term';
  target_type?: string;
  relation: 'is' | 'spans';
  valid_from?: number | null;
  valid_to?: number | null;
  attributes?: Record<string, unknown>;
  mapping_id?: string;
  note?: string;
}

/**
 * A preview/confirm warning as `analyse()` emits it: always a `code`, plus code-specific
 * extras (flat, not nested). Per-item warnings live on `PreviewItem.warnings`.
 */
export interface PreviewWarning {
  code: string;
  mapping_id?: string;
  /** `overlaps_item`: index of the other item. */
  index?: number;
  /** `superseded_seasons_left_unmapped`: the seasons that lose their mapping. */
  seasons?: number[];
  /** `tenant_job_in_flight`. */
  job_ids?: string[];
  /** `dropped_register_keys`. */
  keys?: Record<string, unknown>;
  job_status?: string;
  /** `foreign_source_rows` / `ownership_declaration` extras, when the server adds them. */
  count?: number;
  source_system?: string;
}

export interface PreviewItem {
  action: string;
  noop?: boolean;
  warnings?: PreviewWarning[];
  /** `confirm` items: the live rows this item supersedes (F-16: the first overlapping item). */
  supersedes?: string[];
  /** `confirm` items: rows an earlier item of the batch supersedes that this one overlaps too. */
  also_supersedes?: string[];
  [key: string]: unknown;
}

export interface PreviewCounts {
  [key: string]: unknown;
}

/** Whose row a superseded mapping id is: the TARGET object (a term's own value as its name), never a label. */
export interface SupersededTarget {
  mapping_id: string;
  target_kind: string | null;
  target_type: string | null;
  target_ref: string | null;
  /** null when the object is gone, unnamed or its name could not be read. */
  target_name: string | null;
  /** The superseded row's status: a `proposed` one is a suggestion the batch answers (moves no data). Absent from an older backend. */
  status?: 'proposed' | 'confirmed' | null;
  /** A confirmed row the batch restates verbatim (same target and seasons): moves nothing. */
  identical?: boolean;
  /** The superseded row's own seasons (`null` = open end). Absent from an older backend. */
  valid_from?: number | null;
  valid_to?: number | null;
}

export interface PreviewResponse {
  items: PreviewItem[];
  counts: PreviewCounts;
  rematerialise: unknown;
  warnings: PreviewWarning[];
  would_be_noop: boolean;
  fingerprint: string;
  /** Per superseded id, whose row it is (absent from an older backend). */
  superseded_targets?: SupersededTarget[];
}

export interface ConfirmResponse {
  mapping_ids: string[];
  job_id: string | null;
  counts: PreviewCounts | null;
  warnings: PreviewWarning[];
}

export interface Suggestion {
  entity_id: string;
  name: string;
  tier: 'strong' | 'possible';
  evidence_key: 'same_name' | 'similar_name';
  evidence_params: { tokens?: string[]; crop?: string; variety?: string; area_ha?: number };
  properties: { area_ha?: number; crop?: string };
}

export interface Duplicate {
  entity_id: string;
  name: string;
  evidence_key: 'same_vat_id' | 'same_iban' | 'similar_name';
}

export type MappingJobStatus = 'planned' | 'queued' | 'running' | 'done' | 'refused' | 'failed';

export interface MappingJob {
  job_id: string;
  status: MappingJobStatus;
  outcome: Record<string, unknown> | null;
  created_at: string;
  finished_at: string | null;
}

export interface CropTerm {
  term: string;
  cycles: number;
  mappings: number;
}

export interface SourceSearchHit {
  entity_id: string;
  name: string;
  properties: { area_ha?: number; crop?: string };
}

export const SOURCES_ERROR_KINDS = [
  'sources_unavailable',
  'sources_search_unavailable',
  'crop_terms_unavailable',
  'sources_preview_unavailable',
  'sources_suggestions_unavailable',
  'sources_duplicates_unavailable',
  'sources_reject_unavailable',
  'sources_merge_unavailable',
  'sources_job_unavailable',
  'sources_confirm_unavailable',
  'confirm_partial_failure',
  'merge_not_allowed',
  'merge_same_entity',
  'entity_not_found',
  'job_not_found',
  'mapping_not_proposed',
  'action_not_allowed',
  'tenant_job_in_flight',
  'preview_stale',
  'query_required',
  // UDB kinds a confirm / reject / merge passes through (``_udb_error_kind``)
  'mapping_overlap',
  'registry_violation',
  'supersedes_not_live',
  'mapping_not_live',
  'mapping_not_confirmed',
  'target_not_live',
] as const;

export type SourcesErrorKind = (typeof SOURCES_ERROR_KINDS)[number];

/** The documented `detail.error_kind` of an axios error, or null (plain Error, unknown kind). */
export function sourcesErrorKind(err: unknown): SourcesErrorKind | null {
  const kind = (err as { response?: { data?: { detail?: { error_kind?: unknown } } } } | null)?.response?.data?.detail
    ?.error_kind;
  return typeof kind === 'string' && (SOURCES_ERROR_KINDS as readonly string[]).includes(kind)
    ? (kind as SourcesErrorKind)
    : null;
}

/**
 * The ids a 502 `confirm_partial_failure` names (rows that already landed, and the job when it
 * was created), or null for any other error. The rest of the detail is never read.
 */
export function confirmFailureDetail(
  err: unknown,
): ConfirmFailureDetail | null {
  const detail = (err as { response?: { data?: { detail?: Record<string, unknown> } } } | null)?.response?.data?.detail;
  if (!detail || typeof detail !== 'object' || detail.error_kind !== 'confirm_partial_failure') return null;
  const str = (v: unknown) => (typeof v === 'string' && v !== '' ? v : null);
  const ids = (v: unknown) => (Array.isArray(v) ? v.filter((m): m is string => typeof m === 'string') : []);
  return {
    stage: str(detail.stage),
    mapping_ids: ids(detail.mapping_ids),
    superseded: ids(detail.superseded),
    retired: ids(detail.retired),
    rejected: ids(detail.rejected),
    job_id: str(detail.job_id),
    cause_kind: str(detail.cause_kind),
  };
}

/** A 502 `confirm_partial_failure` body: ids only — created rows (`mapping_ids`), rows superseded,
 *  retired or rejected, and the job — so the caller can tell whether ANYTHING landed. */
export interface ConfirmFailureDetail {
  stage: string | null;
  mapping_ids: string[];
  superseded: string[];
  retired: string[];
  rejected: string[];
  job_id: string | null;
  cause_kind: string | null;
}

const BASE = '/v1/twin/sources';
const seg = encodeURIComponent;

export const sourcesService = {
  async getSources(type: string, id: string): Promise<SourcesResponse> {
    const { data } = await api.get(`${BASE}/${seg(type)}/${seg(id)}`);
    return data;
  },
  /** A vocabulary term's rows. The term rides in the query: a free-text term may contain "/". */
  async getTermSources(vocabulary: string, term: string): Promise<SourcesResponse> {
    const { data } = await api.get(`${BASE}/terms/${seg(vocabulary)}/rows`, { params: { term } });
    return data;
  },
  async search(type: string, q: string): Promise<SourceSearchHit[]> {
    const { data } = await api.get(`${BASE}/search`, { params: { entity_type: type, q } });
    return data;
  },
  async getCropTerms(): Promise<CropTerm[]> {
    const { data } = await api.get(`${BASE}/terms/crop`);
    return data;
  },
  async getSuggestions(type: string, id: string): Promise<Suggestion[]> {
    const { data } = await api.get(`${BASE}/${seg(type)}/${seg(id)}/suggestions`);
    return data;
  },
  async getDuplicates(type: string, id: string): Promise<Duplicate[]> {
    const { data } = await api.get(`${BASE}/${seg(type)}/${seg(id)}/duplicates`);
    return data;
  },
  async getJob(jobId: string): Promise<MappingJob> {
    const { data } = await api.get(`${BASE}/jobs/${seg(jobId)}`);
    return data;
  },
  async preview(items: SourceMappingItem[]): Promise<PreviewResponse> {
    const { data } = await api.post(`${BASE}/preview`, { items });
    return data;
  },
  async confirm(body: { items: SourceMappingItem[]; fingerprint: string; note?: string }): Promise<ConfirmResponse> {
    const { data } = await api.post(`${BASE}/confirm`, body);
    return data;
  },
  async reject(body: { mapping_id: string; note?: string }): Promise<{ mapping_id: string; status: 'rejected' }> {
    const { data } = await api.post(`${BASE}/reject`, body);
    return data;
  },
  async merge(body: {
    entity_type: string;
    loser_id: string;
    winner_id: string;
  }): Promise<{ loser_id: string; winner_id: string; facts_repointed: number }> {
    const { data } = await api.post(`${BASE}/merge`, body);
    return data;
  },
};
